import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { contactApi, getDefaultAccountId } from '../services/api';
import { Recipient, AlertInfo } from '../types';

interface ContactSelectorProps {
  open: boolean;
  onClose: () => void;
  onContactsSelected: (contacts: Recipient[]) => void;
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

interface Contact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  position?: string;
  tags: string[];
  isActive: boolean;
}

const ContactSelector: React.FC<ContactSelectorProps> = ({
  open,
  onClose,
  onContactsSelected,
  showAlert,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      loadContacts();
    }
  }, [open]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (open) {
        loadContacts(searchQuery);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchQuery, open]);

  const loadContacts = async (search?: string) => {
    try {
      setLoading(true);
      const accountId = getDefaultAccountId();
      const response = await contactApi.getByAccount(accountId, 1, 100, search);
      setContacts(response.contacts || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
      showAlert('Failed to load contacts. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleContactToggle = (contactId: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(contacts.map(c => c.id)));
    }
  };

  const handleConfirmSelection = () => {
    const selectedContactsList = contacts.filter(c => selectedContacts.has(c.id));
    const recipients: Recipient[] = selectedContactsList.map(contact => ({
      id: contact.id,
      email: contact.email,
      variables: {
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        company: contact.company || '',
        position: contact.position || '',
        fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email,
      },
    }));

    onContactsSelected(recipients);
    onClose();
    setSelectedContacts(new Set());
    setSearchQuery('');
  };

  const getFullName = (contact: Contact) => {
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'N/A';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon />
          Select Contacts
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search contacts by name, email, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {selectedContacts.size > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {selectedContacts.size} contact{selectedContacts.size !== 1 ? 's' : ''} selected
            </Alert>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedContacts.size > 0 && selectedContacts.size < contacts.length}
                        checked={contacts.length > 0 && selectedContacts.size === contacts.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow
                      key={contact.id}
                      hover
                      onClick={() => handleContactToggle(contact.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedContacts.has(contact.id)}
                          onChange={() => handleContactToggle(contact.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getFullName(contact)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contact.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contact.company || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {contact.tags?.slice(0, 2).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" variant="outlined" />
                          ))}
                          {contact.tags?.length > 2 && (
                            <Chip label={`+${contact.tags.length - 2}`} size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {contacts.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          {searchQuery ? 'No contacts found matching your search.' : 'No contacts available.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          disabled={selectedContacts.size === 0}
        >
          Select {selectedContacts.size} Contact{selectedContacts.size !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactSelector;
